import {
  ISalesforce,
  ISalesforceSchema,
  SettingCardViewAction,
} from '@monetize/types/app';
import { FC, useState } from 'react';
import { useResyncSalesforceProductCatalog } from '../../../../../../api/settingsService';
import { SettingCardEdit } from '../../../../../../components/Settings/SettingCardEdit';
import { SettingCardView } from '../../../../../../components/Settings/SettingCardView';
import { useToast } from '../../../../../../services/toast';
import { CrmConnectionHealth } from '../../useCrmConnectionHealth';
import { CrmConnectionHealthAlert } from '../CrmConnectionHealthAlert';
import { SalesforceFieldMapping } from './SalesforceFieldMapping';
import { SalesforceForm } from './SalesforceForm';

const resyncToastMessages = {
  success: {
    summary: 'Product Catalog Synced Successfully',
    detail: 'Your Product Catalog has been synced to Salesforce.',
    severity: 'success',
  },
  warning: {
    summary: 'Product Catalog Partially Synced',
    detail:
      'One or more records were not synced to Salesforce, try again or contact support for assistance.',
    severity: 'warning',
  },
  error: {
    summary: 'Product Catalog Sync Failed',
    detail:
      'Your Product Catalog has not been synced, try reconnecting or contact support for assistance.',
    severity: 'error',
  },
} as const;

interface SalesforceCrmConnectionProps {
  connection: ISalesforce;
  health: CrmConnectionHealth;
  onAuthorize: (data: ISalesforceSchema) => Promise<void>;
  verifyConnection: () => Promise<void>;
}

export const SalesforceCrmConnection: FC<SalesforceCrmConnectionProps> = ({
  connection,
  health,
  onAuthorize,
  verifyConnection,
}) => {
  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const { addToast } = useToast();
  const { mutateAsync: doResyncProductCatalog } =
    useResyncSalesforceProductCatalog({
      onSuccess: (data) => {
        if (data.syncSummary === 'success') {
          addToast(resyncToastMessages.success);
        } else if (data.syncSummary === 'partial_success') {
          addToast(resyncToastMessages.warning);
        } else {
          addToast(resyncToastMessages.error);
        }
      },
      onError: (err) => {
        addToast(resyncToastMessages.error);
      },
    });

  const handleAction = async (action: SettingCardViewAction) => {
    switch (action) {
      case 'RESYNC-CATALOG':
        return doResyncProductCatalog();
      case 'VERIFY':
        await verifyConnection();
        break;
      case 'DELETE':
        break; // TODO:

      default:
        break;
    }
  };

  return (
    <>
      {/* Re-authenticating Salesforce goes through the form (it needs the instance URL) */}
      <CrmConnectionHealthAlert
        crmLabel="Salesforce"
        health={health}
        onReAuthenticate={() => setMode('EDIT')}
      />
      {mode === 'VIEW' && (
        <SettingCardView
          status={health.status === 'unhealthy' ? 'ERROR' : 'ACTIVE'}
          actions={[
            {
              action: 'RESYNC-CATALOG',
              label: 'Resync Product Catalog',
              isDanger: false,
            },
            { action: 'VERIFY', label: 'Verify Connection' },
            // { // TODO: not yet implemented on BE
            //   action: 'DELETE',
            //   label: 'Delete',
            //   isDanger: true,
            //   confirmProps: {
            //     title: `Are you sure you want to delete this connection?`,
            //     description:
            //       'Once removed, this configuration will not longer be available.',
            //   },
            // },
          ]}
          type="Salesforce"
          tags={[
            {
              label: 'Environment',
              value: connection.environment,
            },
            { label: 'Org Id', value: connection.orgId },
            { label: 'Username', value: connection.username },
            { label: 'Instance URL', value: connection.instanceUrl },
          ]}
          onEdit={() => setMode('EDIT')}
          onAction={handleAction}
        />
      )}
      {mode === 'EDIT' && (
        <SettingCardEdit type="Salesforce">
          <SalesforceForm
            connection={connection}
            onAuthorize={onAuthorize}
            handleCancel={() => setMode('VIEW')}
          />
        </SettingCardEdit>
      )}
      <SalesforceFieldMapping />
    </>
  );
};
